import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Hint } from '@/components/ui/hint';
import { cn } from '@/lib/utils';
import type { IssuanceState } from '@/features/invoices/outbound/api';
import { useBadgeCounts } from './api';

/** Cap a raw count for a compact, fixed-width nav pill. */
const cap = (n: number): string => (n > 99 ? '99+' : String(n));

/** Shared pill shape for every nav count badge — a small bordered, tinted, tabular-number chip. */
const PILL =
  'flex h-5 min-w-5 items-center justify-center rounded-md border px-1 text-xs font-medium tabular-nums';

// Absolutely-positioned badge row, matching the shadcn `SidebarMenuBadge` slot (right-aligned, hidden in
// the collapsed icon rail). The row itself is click-through (`pointer-events-none`) so it never eats a nav
// click; individual pills re-enable pointer events only where a hover tooltip is wanted.
const BADGE_ROW =
  'pointer-events-none absolute right-1 top-1.5 flex items-center gap-0.5 select-none group-data-[collapsible=icon]:hidden';

/** Muted (gray) tone for the neutral drafts count — mirrors the StatusBadge "abandoned/muted" family. */
const MUTED = 'border-border bg-muted text-muted-foreground';

// The global TooltipProvider opens instantly (delayDuration=0) for the collapsed nav labels; the badge
// hints are informational, not navigational, so on a mouse they wait a beat before appearing.
const HOVER_DELAY_MS = 1000;

/**
 * A single count pill with a device-aware {@link Hint}: a hover tooltip with a mouse (after
 * [HOVER_DELAY_MS]), a tap popover on touch. The pill re-enables pointer events (the surrounding
 * `BADGE_ROW` is click-through) so it can receive hover/tap; it sits outside the nav `<Link>`, so a tap
 * to open the breakdown never triggers navigation.
 */
function CountPill({
  count,
  className,
  ariaLabel,
  hint,
}: {
  count: number;
  className: string;
  ariaLabel: string;
  hint: ReactNode;
}) {
  return (
    <Hint content={hint} hoverDelayMs={HOVER_DELAY_MS}>
      <span aria-label={ariaLabel} className={cn(PILL, 'pointer-events-auto', className)}>
        {cap(count)}
      </span>
    </Hint>
  );
}

/**
 * Unread-style count of *started* drafts on the "Drafts" nav item, served by the unified badge-count
 * endpoint (one poll feeds every sidebar badge). Rendered as a neutral gray pill matching the "Sent"
 * state pills, with a hover tooltip naming the count. Renders nothing at zero so the item stays clean,
 * and only ever mounts inside the issuing group (gated on `me.issuingEnabled`). The count is
 * server-authoritative and excludes the pristine "new invoice" placeholder, so it never over-counts by
 * one. Auto-hides in the collapsed icon rail.
 */
export function DraftsNavBadge() {
  const { t } = useTranslation();
  const { data } = useBadgeCounts();
  const count = data?.drafts ?? 0;
  if (count === 0) return null;
  const label = `${t('nav.drafts')}: ${count}`;
  return (
    <div className={BADGE_ROW}>
      <CountPill count={count} className={MUTED} ariaLabel={label} hint={label} />
    </div>
  );
}

/**
 * The outbound-state groups surfaced as pills on the "Sent" nav item, in display order. Each maps to a
 * StatusBadge colour family; the aggregation and colouring live here (not server-side) so the scheme can
 * change without an API change. Terminal / issued states (ACCEPTED, COMPLETED, ABANDONED) are
 * intentionally omitted — they only grow and need no user attention.
 */
const SENT_GROUPS: { key: string; states: IssuanceState[]; className: string }[] = [
  {
    key: 'in_flight',
    states: ['QUEUED', 'SUBMITTING', 'AWAITING_KSEF'],
    className: 'border-sky-500/40 bg-sky-500/15 text-sky-700 dark:text-sky-300',
  },
  {
    key: 'attention',
    states: ['NEEDS_ATTENTION', 'RETRY_WAIT'],
    className: 'border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300',
  },
  {
    key: 'rejected',
    states: ['REJECTED'],
    className: 'border-destructive/40 bg-destructive/15 text-destructive',
  },
];

/**
 * Per-group count pills on the "Sent" nav item: sky (in-flight), amber (needs attention / retry-wait),
 * red (rejected). A group's pill shows only while its count is non-zero, so a fully-settled register
 * shows nothing at all. Hovering a pill opens a tooltip with the per-state breakdown. Auto-hides in the
 * collapsed rail.
 */
export function SentNavBadge() {
  const { t } = useTranslation();
  const { data } = useBadgeCounts();
  const outbound = data?.outbound;
  if (!outbound) return null;

  const pills = SENT_GROUPS.map((group) => ({
    ...group,
    present: group.states.filter((state) => (outbound[state] ?? 0) > 0),
    count: group.states.reduce((sum, state) => sum + (outbound[state] ?? 0), 0),
  })).filter((pill) => pill.count > 0);
  if (pills.length === 0) return null;

  return (
    <div className={BADGE_ROW}>
      {pills.map((pill) => (
        <CountPill
          key={pill.key}
          count={pill.count}
          className={pill.className}
          ariaLabel={`${t(`nav.sent_badge.${pill.key}`)}: ${pill.count}`}
          hint={
            <div className="flex flex-col gap-0.5">
              {pill.present.map((state) => (
                <span key={state}>
                  {t(`invoices.outbound.state.${state}`)}: {outbound[state] ?? 0}
                </span>
              ))}
            </div>
          }
        />
      ))}
    </div>
  );
}
